import { LocalStorageKeys, AuthResponseCodes } from '@constants/enums';
import { TIMEOUTS, TOKEN_TYPES, ERROR_CODES } from '@constants/auth.constants';
import { ROUTES } from '@configs/routes';
import { HttpStatusCode } from 'axios';
import authManager from '@efex-services/auth/authManager';
import authSvc from '@efex-services/auth/authSvc';
import { getAuthMethodName, isLegacyAuth } from '@efex-services/auth/authUtils';
import mixpanelSvc from '@efex-services/mixpanel/mixpanelSvc';
import useBoolean from '@main-hooks/useBoolean';
import useIsLogged from '@main-hooks/useIsLogged';
import JwtParser from '@utils/JwtTokenManager';
import { useFormik } from 'formik';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { authenticate, cognitoAuthenticate } from '../auth';
import { ACTION } from '../constants';
import {
  LoginProps,
  UseLoginServiceFormik,
  UseLoginServiceReturn,
} from '../types';
import useClearStorageService from './useClearStorage.service';
import useWhiteLabelService from './useWhiteLabel.service';

const useLoginService = (): UseLoginServiceReturn => {
  const { t } = useTranslation();
  const jwtParser = new JwtParser();
  const location = useLocation();
  const {
    whiteLabelState,
    getWhiteLabel,
    isWhiteLabel,
    ...restWhiteLabelService
  } = useWhiteLabelService();
  const { clearWhiteLabelStorage } = useClearStorageService();

  const [success, setSuccess] = useState(false);
  const [messageData, setMessageData] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lgResp, setLgResp] = useState<LoginProps | undefined>(undefined);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isUserLogged = useIsLogged();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);

  const [showTwoFactorValidation, { toggle: toggleTwoFactor }] =
    useBoolean(false);
  const [isLoginProcess, { toggle: toggleLoginProcess }] = useBoolean(false);
  const [showPasswordResetModal, { toggle: togglePasswordResetModal }] =
    useBoolean(false);
  const { boot, shutdown } = useIntercom();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const saveEmailFromInput = () => {
    const resetEmail = (document.getElementById('email') as HTMLInputElement)
      .value;
    localStorage.setItem(LocalStorageKeys.RESET_EMAIL_PASSWORD, resetEmail);
  };

  const removeQueryParams = (name: string) => {
    const nameParam = searchParams.get(name);
    if (nameParam) {
      searchParams.delete(name);
      setSearchParams(searchParams);
    }
  };

  const goToRegisterPage = () => {
    clearWhiteLabelStorage();
    saveEmailFromInput();
    setTimeout(() => {
      navigate(ROUTES.AUTH.REGISTER);
    }, TIMEOUTS.NAVIGATION_DELAY);
    mixpanelSvc.track('Click register');
  };

  const handleResetPassword = () => {
    saveEmailFromInput();
    setTimeout(() => {
      navigate(ROUTES.AUTH.RESET);
    }, TIMEOUTS.NAVIGATION_DELAY);
  };

  const dispatchLegacyUserData = (authResponse: any) => {
    dispatch(authenticate(authResponse));
  };

  const dispatchCognitoUserData = (authResponse: any) => {
    dispatch(cognitoAuthenticate(authResponse));
  };

  const validateUserData = async (values: LoginProps) => {
    if (!values.loginResponse.token) return;
    try {
      const response = await authSvc.userValidate();
      if (response.isActive) {
        if (!response.isTestUser) {
          mixpanelSvc.identify(values.email);
          mixpanelSvc.track('Successful login');
        }

        toggleLoginProcess();
        dispatchLegacyUserData({
          ...response,
          refresh: values.loginResponse.refresh,
        });
      } else {
        navigate(ROUTES.SYSTEM.UNDER_REVISION);
      }
    } catch (e) {
      console.error(e);
      navigate(ROUTES.SYSTEM.UNDER_REVISION);
      setMessageData(values.loginResponse.non_field_errors);
    }
  };

  const handleLegacyLogin = async (
    result: any,
    values: { email: string; password: string }
  ) => {
    if (!result.token) {
      toggleLoginProcess();
      setMessageData(t('errorLoginInvalidCredentials') || '');
      return;
    }

    setSuccess(true);
    shutdown();
    boot();

    mixpanelSvc.identify(values.email);
    mixpanelSvc.track(`Successful ${getAuthMethodName()} login`);

    setLgResp({
      email: values.email,
      password: values.password,
      loginResponse: result,
    });

    const parsedToken = jwtParser.parse(result.token);
    if (parsedToken?.token_type === TOKEN_TYPES.ACCESS_2FA) {
      toggleTwoFactor();
    } else {
      await validateUserData({
        email: values.email,
        password: values.password,
        loginResponse: result,
      });
    }
  };

  const handleCognitoLogin = async (
    result: any,
    values: { email: string; password: string }
  ) => {
    if (result.code === AuthResponseCodes.FORCE_PASSWORD_RESET_MIGRATION) {
      if (!result.idToken) {
        toggleLoginProcess();
        setMessageData(t('errorPasswordMigrationFailed') || '');
        return;
      }

      localStorage.setItem(LocalStorageKeys.RESET_EMAIL_PASSWORD, values.email);
      localStorage.setItem(LocalStorageKeys.MIGRATION_ID_TOKEN, result.idToken);

      toggleLoginProcess();
      togglePasswordResetModal();
      return;
    }

    if (
      result.code &&
      result.code !== AuthResponseCodes.FORCE_PASSWORD_RESET_MIGRATION &&
      result.code !== AuthResponseCodes.SUCCESS
    ) {
      toggleLoginProcess();
      setMessageData(
        `${t('errorAuthenticationFailed') || ''}: ${
          result.details || t('errorDefault') || ''
        }`
      );
      return;
    }

    if (!result.idToken) {
      toggleLoginProcess();
      setMessageData(t('errorLoginInvalidCredentials') || '');
      return;
    }

    setSuccess(true);
    shutdown();
    boot();

    mixpanelSvc.identify(values.email);
    mixpanelSvc.track(`Successful ${getAuthMethodName()} login`);

    toggleLoginProcess();
    dispatchCognitoUserData(result);
  };

  const handleUnifiedLogin = async (values: {
    email: string;
    password: string;
  }) => {
    try {
      const result = await authManager.login({
        email: values.email,
        password: values.password,
      });

      if (isLegacyAuth()) {
        await handleLegacyLogin(result, values);
      } else {
        await handleCognitoLogin(result, values);
      }
    } catch (error) {
      toggleLoginProcess();
      setMessageData(t('errorLoginInvalidCredentials') || '');
    }
  };

  const handleLogin = async (values: { email: string; password: string }) => {
    try {
      await handleUnifiedLogin(values);
    } catch (error: any) {
      toggleLoginProcess();

      const isPasswordResetRequired =
        error.response?.status === HttpStatusCode.Forbidden ||
        error.status === HttpStatusCode.Forbidden ||
        error.code === ERROR_CODES.NETWORK ||
        error.code === ERROR_CODES.FAILED ||
        (error.message &&
          error.message.includes(HttpStatusCode.Forbidden.toString()));

      if (isPasswordResetRequired) {
        togglePasswordResetModal();
        return;
      }

      if (error.response?.data?.message) {
        setMessageData(error.response.data.message);
      } else if (error.response?.data?.non_field_errors) {
        setMessageData(error.response.data.non_field_errors);
      } else {
        setMessageData(t('errorUnexpectedLogin') || '');
      }
    }
  };

  useEffect(() => {
    if (location.state?.message) {
      if (location.state?.isSuccessMessage) {
        setSuccessMessage(location.state.message);
      } else {
        setMessageData(location.state.message);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const formik: UseLoginServiceFormik = useFormik({
    initialValues: {
      email: location.state?.email || '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required(t('emailRequired') || '')
        .email(t('emailInvalid') || ''),
      password: Yup.string().required(t('passwordRequired') || ''),
    }),

    onSubmit: async (values: { email: string; password: string }) => {
      setSuccess(false);
      setMessageData('');
      setSuccessMessage('');
      toggleLoginProcess();
      removeQueryParams(ACTION);

      await handleLogin(values);
    },
  });

  return {
    success,
    messageData,
    successMessage,
    lgResp,
    isUserLogged,
    showPassword,
    showTwoFactorValidation,
    showPasswordResetModal,
    isLoginProcess,
    formik,
    searchParams,
    isWhiteLabel: !!isWhiteLabel,
    whiteLabelState,
    handleClickShowPassword,
    goToRegisterPage,
    handleResetPassword,
    toggleTwoFactor,
    togglePasswordResetModal,
    validateUserData,
    getWhiteLabel,
    handleCognitoLogin,
    ...restWhiteLabelService,
  };
};

export default useLoginService;
